import { useState, useEffect, useCallback } from "react";
import { T, F, Over, Pill, Btn } from "@/lib/bricks";
import { toast } from "sonner";

// RECO annual financial filing support + O. Reg. 579/05 s.19 six-year retention.
// Bricks prepares the evidence pack and readable exports. Filing and attestation happen in
// RECO MyWeb, by the broker of record. Bricks sends nothing to RECO.
const cad = (cents) => new Intl.NumberFormat('en-CA', { style:'currency', currency:'CAD' }).format((cents || 0) / 100);
const day = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-CA', { month:'short', day:'numeric', year:'numeric' }) : '—';
const when = (ts) => ts ? new Date(ts).toLocaleString('en-CA', { month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit' }) : '—';
const STATUS = {
  evidence_ready: ['ok', 'Evidence ready'],
  gap: ['brick', 'Gap: explain in MyWeb'],
  review: ['warn', 'BoR to review'],
  outside_bricks: ['neutral', 'Outside Bricks'],
};
const MONTH = { signed:['ok','Signed'], draft:['warn','Not signed'], missing:['brick','Missing'], not_applicable:['neutral','n/a'] };

export default function TrustYearEnd({ BASE, headers, p, dark }) {
  const [fye, setFye] = useState('');
  const [pack, setPack] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [range, setRange] = useState({ start:'', end:'' });
  const [busy, setBusy] = useState('');
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    const q = fye ? `?fiscal_year_end=${fye}` : '';
    fetch(`${BASE}/api/accounting/trust/year-end${q}`, { headers })
      .then(r => r.ok ? r.json() : null).then(d => { setPack(d); if (d && !fye) setFye(d.fiscal_year_end); }).catch(() => setPack(null));
  }, [BASE, headers.Authorization, fye]); // eslint-disable-line
  const loadPolicy = useCallback(() => {
    fetch(`${BASE}/api/accounting/trust/records-policy`, { headers }).then(r => r.ok ? r.json() : null).then(setPolicy).catch(() => setPolicy(null));
  }, [BASE, headers.Authorization]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPolicy(); }, [loadPolicy]);

  const download = async (path, fallback, label) => {
    setBusy(label);
    try {
      const res = await fetch(`${BASE}${path}`, { headers });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === 'string' ? d.detail : 'Export failed');
      }
      const name = (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || fallback;
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('Downloaded. The export is logged in the trust audit trail.');
      load(); loadPolicy();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(''); }
  };
  const savePolicy = async () => {
    setBusy('policy');
    try {
      const res = await fetch(`${BASE}/api/accounting/trust/records-policy`, { method:'PUT', headers, body: JSON.stringify({
        retention_years: edit.retention_years === '' ? null : Number(edit.retention_years),
        retention_policy: edit.policy_text, fiscal_year_end: edit.fiscal_year_end }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Couldn’t save');
      toast.success('Records policy saved');
      setEdit(null); setFye(''); loadPolicy(); load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(''); }
  };

  if (!pack) return null;
  const fig = pack.filing_figures || {};
  const rec = pack.recons || {};
  const row = { padding:'12px var(--bx-pad)', borderBottom:`1px solid ${p.hair}`, fontSize:13 };
  const input = { padding:'8px 10px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:13, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' };
  const mono = { fontFamily:F.mono, color:p.ink };
  const lastExport = (pack.exports || [])[0];

  return (
    <div style={{ border:`1px solid ${p.hair}`, borderRadius:2 }}>
      <div style={{ padding:'16px var(--bx-pad)', borderBottom:`1px solid ${p.hair}`, display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <div>
          <Over color={p.ink3}>Year-end filing pack · RECO annual financial filing</Over>
          <div style={{ fontSize:13, color:p.ink2, marginTop:4, maxWidth:620 }}>
            Bricks prepares the evidence from your trust records. The broker of record files and attests in RECO MyWeb. Bricks doesn’t file, attest or send anything to RECO. The ZIP includes PDF summaries plus CSV detail.
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <select style={input} value={fye} onChange={e => setFye(e.target.value)} aria-label="Fiscal year end">
            {(pack.fiscal_year_choices || []).map(f => <option key={f} value={f}>Year ending {day(f)}</option>)}
            {fye && !(pack.fiscal_year_choices || []).includes(fye) && <option value={fye}>Year ending {day(fye)}</option>}
          </select>
          <Btn variant="primary" dark={dark} disabled={!!busy} onClick={() => download(`/api/accounting/trust/year-end/export.zip?fiscal_year_end=${pack.fiscal_year_end}`, `trust-year-end-${pack.fiscal_year_end}.zip`, 'pack')}>
            {busy === 'pack' ? 'Preparing…' : 'Generate & download pack'}
          </Btn>
        </div>
      </div>

      <div style={{ ...row, display:'flex', gap:24, flexWrap:'wrap', color:p.ink3, fontSize:12 }}>
        <span>Fiscal year {day(pack.fiscal_year_start)} – {day(pack.fiscal_year_end)}</span>
        <span>Filing due in MyWeb <span style={{ ...mono, color: pack.filing_overdue ? T.brick : p.ink }}>{day(pack.filing_due)}</span>{pack.filing_overdue ? ' · past due' : ''}</span>
        {!pack.year_complete && <span style={{ color:T.warn }}>This year hasn’t ended: figures are a preview</span>}
        <span>{lastExport ? `Last pack ${when(lastExport.created_at)} by ${lastExport.generated_by_name || '—'}` : 'No pack generated for this year yet'}</span>
      </div>

      {/* 12 signed reconciliations */}
      <div style={row}>
        <div style={{ display:'flex', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <div style={{ color:p.ink }}>Monthly reconciliations signed by the broker of record: <span style={mono}>{rec.signed_count || 0} of {rec.required_count || 0}</span></div>
          <Pill tone={rec.complete ? 'ok' : 'brick'} dark={dark}>{rec.complete ? 'All month ends signed' : `${(rec.gaps || []).length} gap${(rec.gaps || []).length === 1 ? '' : 's'}`}</Pill>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(92px, 1fr))', gap:6, marginTop:10 }}>
          {(rec.months || []).slice().sort((a, b) => a.period.localeCompare(b.period)).map(m => {
            const [tone, label] = MONTH[m.status] || ['neutral', m.status];
            return (
              <div key={m.period} title={m.signed_at ? `Signed ${when(m.signed_at)} by ${m.signed_by_name || ''}${m.signed_late ? ' (after the 30-day deadline)' : ''}` : label}
                   style={{ border:`1px solid ${p.hair}`, borderRadius:2, padding:'6px 8px' }}>
                <div style={{ fontFamily:F.mono, fontSize:11, color:p.ink }}>{m.period}</div>
                <div style={{ fontSize:12, color: tone === 'ok' || tone === 'neutral' ? p.ink3 : T.brickDeep, marginTop:2 }}>{label}{m.signed_late ? ', late' : ''}</div>
              </div>
            );
          })}
        </div>
        {(rec.gaps || []).length > 0 && <div style={{ fontSize:11, color:p.ink3, marginTop:8 }}>{rec.gaps.map(g => `${g.period}: ${g.reason}`).join(' · ')}</div>}
      </div>

      {/* Filing figures */}
      <div style={{ ...row, display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:14 }}>
        {[
          ['Total trust liabilities', cad(fig.total_trust_liabilities_cents), 'Balances owing per deal at year end'],
          ['Total trust assets', cad(fig.total_trust_assets_cents), fig.assets_source_label],
          ['Balance of trust assets', cad(fig.balance_of_trust_assets_cents), 'Assets minus liabilities'],
          ['Unclaimed trust money', cad(fig.total_unclaimed_cents), `Unclear ${fig.unclaimed_unclear_count || 0} · can’t locate ${fig.unclaimed_unlocated_count || 0} · over 2 yrs ${cad(fig.unclaimed_over_two_years_cents)}`],
          ['Interest retained by brokerage', cad(fig.interest_retained_by_brokerage_cents), 'Only where the contract gives it to the brokerage'],
          ['BoR decisions in year', `${pack.approvals?.approved_count || 0} approved · ${pack.approvals?.rejected_count || 0} rejected`, (pack.approvals?.unauthorized_in_period_ids || []).length ? `${pack.approvals.unauthorized_in_period_ids.length} entry(ies) still unapproved` : 'Every counted entry approved'],
          ['Shortfalls logged', `${pack.shortfalls?.count || 0}`, (pack.shortfalls?.count ? `${(pack.shortfalls.not_notified || []).length} not reported · ${(pack.shortfalls.not_topped_up || []).length} not topped up` : 'None in the year')],
          ['Completed deals', `${fig.completed_deals_in_year || 0}`, 'Count ends in MyWeb: double-ended = 2'],
        ].map(([k, v, s]) => (
          <div key={k}><Over color={p.ink3}>{k}</Over><div style={{ ...mono, fontSize:15, marginTop:6 }}>{v}</div><div style={{ fontSize:11, color:p.ink3, marginTop:2 }}>{s}</div></div>
        ))}
      </div>

      {/* Liability by beneficiary */}
      {(pack.liability?.by_beneficiary || []).length > 0 && (
        <div style={row}>
          <Over color={p.ink3}>Owing to each person at {day(pack.fiscal_year_end)}</Over>
          {pack.liability.by_beneficiary.slice(0, 12).map(b => (
            <div key={b.beneficiary} style={{ display:'grid', gridTemplateColumns:'1.4fr 2fr auto', gap:12, marginTop:8, fontSize:12 }}>
              <div style={{ color:p.ink }}>{b.beneficiary}</div>
              <div style={{ color:p.ink3 }}>{b.deals.join(' · ')}</div>
              <div style={mono}>{cad(b.balance_cents)}</div>
            </div>
          ))}
          {pack.liability.by_beneficiary.length > 12 && <div style={{ fontSize:11, color:p.ink3, marginTop:6 }}>+{pack.liability.by_beneficiary.length - 12} more in the pack</div>}
        </div>
      )}

      {/* Attestation checklist */}
      <div style={row}>
        <Over color={p.ink3}>What the broker of record still attests in MyWeb</Over>
        {(pack.attestations || []).map((a, i) => {
          const [tone, label] = STATUS[a.status] || ['neutral', a.status];
          return (
            <div key={a.key} style={{ display:'grid', gridTemplateColumns:'24px 1fr auto', gap:12, alignItems:'start', marginTop:10 }}>
              <div style={{ fontFamily:F.mono, fontSize:11, color:p.ink3 }}>{String(i + 1).padStart(2, '0')}</div>
              <div>
                <div style={{ color:p.ink, fontSize:13 }}>{a.attestation}</div>
                <div style={{ color:p.ink3, fontSize:11, marginTop:2 }}>{a.evidence}</div>
              </div>
              <Pill tone={tone} dark={dark}>{label}</Pill>
            </div>
          );
        })}
        <div style={{ fontSize:11, color:p.ink3, marginTop:12 }}>
          Not in Bricks: {(pack.outside_bricks || []).join(' · ')}.
        </div>
      </div>

      {/* Retention + inspector export */}
      <div style={{ padding:'16px var(--bx-pad)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', gap:12, flexWrap:'wrap', alignItems:'flex-start' }}>
          <div style={{ maxWidth:620 }}>
            <Over color={p.ink3}>Record retention · O. Reg. 579/05 s.19</Over>
            <div style={{ fontSize:13, color:p.ink2, marginTop:4 }}>
              Kept at least <span style={mono}>{policy?.retention_years || 6} years</span>{policy?.oldest_record ? ` · oldest record ${day(policy.oldest_record)}, kept until at least ${day(policy.oldest_kept_until)}` : ''}. Fiscal year ends {policy?.fiscal_year_end || '12-31'}.
            </div>
            <div style={{ fontSize:11, color:p.ink3, marginTop:4 }}>{policy?.deletion}</div>
          </div>
          {policy?.can_manage && !edit && (
            <button style={{ background:'none', border:`1px solid ${p.hair}`, borderRadius:2, padding:'4px 10px', cursor:'pointer', fontSize:11, fontFamily:F.mono, color:p.ink2 }}
                    onClick={() => setEdit({ retention_years: policy.retention_years, policy_text: policy.policy_text, fiscal_year_end: policy.fiscal_year_end })}>Edit policy</button>
          )}
        </div>
        {edit && (
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginTop:12, maxWidth:620 }}>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <label style={{ fontSize:12, color:p.ink3 }}>Keep for (years, min 6)<br/><input type="number" min={6} max={50} style={{ ...input, width:120, marginTop:4 }} value={edit.retention_years} onChange={e => setEdit({ ...edit, retention_years: e.target.value })}/></label>
              <label style={{ fontSize:12, color:p.ink3 }}>Fiscal year end (MM-DD)<br/><input style={{ ...input, width:120, marginTop:4 }} value={edit.fiscal_year_end} onChange={e => setEdit({ ...edit, fiscal_year_end: e.target.value })}/></label>
            </div>
            <textarea rows={4} style={{ ...input, width:'100%', resize:'vertical' }} value={edit.policy_text} onChange={e => setEdit({ ...edit, policy_text: e.target.value })}/>
            <div style={{ display:'flex', gap:8 }}>
              <Btn variant="primary" dark={dark} disabled={busy === 'policy'} onClick={savePolicy}>Save policy</Btn>
              <Btn variant="ghost" dark={dark} onClick={() => setEdit(null)}>Cancel</Btn>
            </div>
          </div>
        )}
        <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap', marginTop:14 }}>
          <label style={{ fontSize:12, color:p.ink3 }}>From<br/><input type="date" style={{ ...input, marginTop:4 }} value={range.start} max={policy?.today} onChange={e => setRange({ ...range, start: e.target.value })}/></label>
          <label style={{ fontSize:12, color:p.ink3 }}>To<br/><input type="date" style={{ ...input, marginTop:4 }} value={range.end} max={policy?.today} onChange={e => setRange({ ...range, end: e.target.value })}/></label>
          <Btn variant="ghost" dark={dark} disabled={!!busy} onClick={() => {
            const q = new URLSearchParams(); if (range.start) q.set('start', range.start); if (range.end) q.set('end', range.end);
            download(`/api/accounting/trust/records/export.zip${q.toString() ? `?${q}` : ''}`, 'trust-records.zip', 'records');
          }}>{busy === 'records' ? 'Preparing…' : (range.start || range.end ? 'Export this period' : 'Export all trust records')}</Btn>
        </div>
        <div style={{ fontSize:11, color:p.ink3, marginTop:8 }}>
          ZIP includes PDF summaries plus CSV detail for an inspector: cover PDF, ledger, deal sub-ledgers and balance owing per person, reconciliations, trade record sheets, shortfall log, unclaimed money, disclosures and interest, audit log, with a SHA-256 manifest. Bricks does not file to RECO.
        </div>
        {(policy?.exports || []).length > 0 && (
          <div style={{ fontSize:11, color:p.ink3, marginTop:8 }}>
            Recent exports: {policy.exports.slice(0, 4).map(x => `${x.kind === 'year_end_pack' ? 'Year-end pack' : 'Records'} ${day(x.range_start)}–${day(x.range_end)} · ${when(x.created_at)} · ${x.generated_by_name || '—'}`).join(' | ')}
          </div>
        )}
      </div>
    </div>
  );
}
